import { supabase } from "@/lib/supabase";

/**
 * EchoTwin API worker(api.echotwin.kr) 클라이언트.
 *
 * 강스터디 프론트가 EchoTwin 의 매물 상세·분석·시세를 화면에 그리는 통로다.
 * **DB 를 직접 읽지 않는다** — worker 가 service_role 로 DB 를 읽고, 가공·인증·
 * 필드 통제를 거친 결과만 내려준다. 그래서:
 *   - 원본(auction_cases raw)을 통째로 빼갈 수 없다(worker 가 필드·건수를 통제)
 *   - 데이터를 훼손할 수 없다(읽기 엔드포인트만 노출)
 *   - 회원/크레딧 게이팅이 worker 에 이미 걸려 있어 우회 불가
 *
 * 인증: EchoTwin 과 **auth.users 를 공유**하므로, 강스터디 로그인 토큰을 그대로
 * Authorization 헤더에 실으면 worker 가 검증한다(별도 로그인 불필요).
 *
 * 배포 도메인(study.echotwin.kr)은 worker CORS 허용 목록에 등록돼 있다.
 * 로컬 개발(localhost:3000)도 허용돼 있어 그대로 호출된다.
 */
const BASE = import.meta.env.VITE_ECHOTWIN_API_BASE ?? "https://api.echotwin.kr";

export interface EchotwinApiError {
  status: number;
  code?: string;
}

async function authHeader(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * GET 호출. 로그인 상태면 토큰을 자동으로 싣는다(회원 전용 엔드포인트용).
 * 비회원이 회원 전용 엔드포인트를 부르면 worker 가 401 을 준다 — 그건 정상 동작.
 */
export async function echotwinGet<T = unknown>(
  path: string,
  params?: Record<string, string | number | undefined>,
): Promise<T> {
  const url = new URL(path.startsWith("http") ? path : `${BASE}${path}`);
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== "") url.searchParams.set(k, String(v));
    }
  }
  const res = await fetch(url.toString(), {
    headers: { ...(await authHeader()) },
  });
  if (!res.ok) {
    let code: string | undefined;
    try {
      code = (await res.json())?.error;
    } catch {
      /* 본문이 JSON 이 아니면 무시 */
    }
    const err: EchotwinApiError = { status: res.status, code };
    throw err;
  }
  return res.json() as Promise<T>;
}

/**
 * 알려진 엔드포인트 몇 개(2026-08-18 기준). 화면 요구가 정해지면 오너가 worker 에
 * 엔드포인트를 추가하고 여기에 래퍼를 늘린다.
 *
 * - landingSample: 비회원 공개 — 조건별 대표 매물 1건 + 통계(랜딩용).
 * - casePreview: 비회원 공개 — 사건 요약(상세 일부, 잠긴 필드는 안 옴).
 * - 회원 전용(products, cases 상세 등)은 로그인 토큰이 있어야 200.
 */
/**
 * POST 호출(추천 등). 로그인 토큰 자동 첨부.
 */
export async function echotwinPost<T = unknown>(
  path: string,
  body?: unknown,
): Promise<T> {
  const url = path.startsWith("http") ? path : `${BASE}${path}`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(await authHeader()) },
    body: JSON.stringify(body ?? {}),
  });
  if (!res.ok) {
    let code: string | undefined;
    try { code = (await res.json())?.error; } catch { /* noop */ }
    const err: EchotwinApiError = { status: res.status, code };
    throw err;
  }
  return res.json() as Promise<T>;
}

/** 공개 목록 카드 한 건 — GET /api/v1/public/cases 의 items[] 원소. */
export interface PublicCaseCard {
  id: string;                    // 사건 UUID — casePreview(id)/caseDetail(id) 로 이어 부른다
  area: string;                  // 주소 앞 3토막(예: "서울특별시 노원구 중계동")
  propertyType: string;          // apartment|multi_family|officetel|row_house|multi_unit|detached…
  minBidEok: number;             // 최저매각가(억)
  appraisedEok: number;          // 감정가(억)
  discountPct: number | null;    // 최저가/감정가 %(낮을수록 유찰 많음)
  dday: number;                  // 매각기일까지 남은 일수(D-)
  auctionDate: string;           // 매각기일(YYYY-MM-DD)
  marketEstEok: number | null;   // 추정시세(억) — 실거래 비교, 없으면 null
  marketBasis: "building" | "dong" | "appraisal" | "naver" | null; // 시세 근거
  dealCount: number;             // 시세 산정에 쓴 실거래 건수
  marginPct: number | null;      // 최저가 대비 예상차익 % — 신뢰불가(priceWarn)면 null
  priceWarn: boolean;            // 차익 신뢰불가(지분매각·깡통전세·통합감정) — true면 차익 숨김 권장
  riskConfirmed: boolean;        // 권리 위험 분석이 실제로 끝났는지
  riskCount: number;             // 치명 위험 건수(0=없음)
  areaM2: number | null;         // 전용면적(㎡)
  hasPhoto: boolean;             // 외부 사진 보유(카드 썸네일 casePhotoUrl 로)
}

/** GET /api/v1/public/cases 응답(worker layered 래핑을 벗긴 data 부분). */
export interface PublicCasesResult {
  total: number;                 // 조건에 맞는 총 건수(페이지 아님)
  page: number;
  pageSize: number;
  capped: boolean;               // 면적/차익 정렬에서 매칭>300 → 임박순 상위 300만 랭크됨
  items: PublicCaseCard[];
}

export const echotwinApi = {
  landingSample: (params: { region?: string; type?: string; budget?: string; disc?: number }) =>
    echotwinGet("/api/v1/public/landing-sample", params),

  /**
   * 공개 매물 목록/검색 — 전국 활성 매물(신건 제외·미낙찰·유찰)을 조건·페이지로.
   * 로그인 없이 호출된다(공개 필드만). 카드의 id 로 casePreview/caseDetail 연결.
   *
   *   type   : "아파트"|"다세대"|"오피스텔"|"연립"|"다가구"|"단독"(한글, 생략=전체)
   *   region : 주소 부분일치(예: "서울", "노원구")
   *   budgetMin/budgetMax : 최저매각가 범위(원). 예: 300000000 = 3억
   *   disc   : 할인율 하한(%). 예: 30 → 감정가 대비 30%+ 떨어진 물건만
   *   area   : 평형밴드 1(~10평)·2(10~20)·3(20~30)·4(30평+)
   *   sort   : "auction"(기일임박, 기본)|"discount"(할인순)|"margin"(예상차익순)
   *   page/pageSize : 페이지(1부터)/쪽당 건수(기본 20, 최대 50)
   */
  searchCases: (params: {
    region?: string; type?: string;
    budgetMin?: number; budgetMax?: number;
    disc?: number; area?: 1 | 2 | 3 | 4;
    sort?: "auction" | "discount" | "margin";
    page?: number; pageSize?: number;
  }): Promise<{ data: PublicCasesResult }> =>
    echotwinGet("/api/v1/public/cases", params),

  casePreview: (id: string) => echotwinGet(`/api/v1/public/case-preview/${id}`),
  casePhotoUrl: (id: string, seq: number) =>
    `${BASE}/api/v1/public/case-photo/${id}?seq=${seq}`,
  caseAppraisalPhotoUrl: (id: string, seq: number) =>
    `${BASE}/api/v1/public/case-appraisal-photo/${id}?seq=${seq}`,

  // ── 회원 전용(로그인 토큰 자동 첨부) ──────────────────────────────
  // 매물상세 전체(분석·위험·입찰가·세후). data.entitled=false 면 심층 잠김.
  caseDetail: (id: string) => echotwinGet(`/api/v1/cases/${id}`),
  similarProperties: (id: string) => echotwinGet(`/api/v1/cases/${id}/similar-properties`),
  relatedByLocation: (id: string) => echotwinGet(`/api/v1/cases/${id}/related-by-location`),
  relatedByCreditor: (id: string) => echotwinGet(`/api/v1/cases/${id}/related-by-creditor`),

  // 맞춤추천 — 프로필 기반 랭킹. data.ranked[] 의 caseId 로 상세를 이어 부른다.
  recommendations: (body?: Record<string, unknown>) =>
    echotwinPost("/api/v1/recommendations", body),

  // Twin 프로필(투자 성향)
  twinProfile: () => echotwinGet("/api/v1/twin/profile"),
  saveTwinProfile: (profile: Record<string, unknown>) =>
    echotwinGet("/api/v1/twin/profile"), // PUT 은 필요 시 echotwinPost 확장

  // 상품(물건 분류)
  products: () => echotwinGet("/api/v1/products"),
  productCases: (id: string) => echotwinGet(`/api/v1/products/${id}/cases`),

  // 크레딧·구독
  credits: () => echotwinGet("/api/v1/credits"),
  subscription: () => echotwinGet("/api/v1/subscriptions/me"),
};
